import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTeachers } from '../hooks/useUsersState';
import { useUsersActions } from '../hooks/useUsersActions';
import { ListTable, SubPage, Button, IconAsButton } from '../../../ui';
import Loading from '../../../ui/Loading';
import ConfirmDelete from '../../../ui/Modal/ConfirmDelete';
import InviteTeacher from '../../invites/components/InviteTeacher';
import UserModal from './UserModal';
import PageHeader from '../../../ui/Page/PageHeader';
import PageControls from '../../../ui/Page/PageControls';
import { useRefetchOnAuthChange } from '../../auth/hooks';

const TeachersList = ({ modal }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [searchQuery, setSearchQuery] = useState('');
  const [userToDelete, setUserToDelete] = useState(null);
  const [isModalOpen, setIsModelOpen] = useState(false);

  const { teachers: users = [], isLoading, refetch } = useTeachers();
  useRefetchOnAuthChange(refetch);
  const { removeUserFromStore } = useUsersActions();

  const openUser = useCallback((obj) => {
    navigate(`/users/teachers/${obj.id}`)
  }, [navigate]);

  // FIX: Use useMemo instead of useEffect to avoid infinite loop from users array changing
  const localUsers = useMemo(() => {
    return users.filter((el) => el.name.toLowerCase().includes(searchQuery))
  }, [users, searchQuery]);

  const handleSearch = useCallback((value) => {
    setSearchQuery(value)
  }, []);

  const clearSearch = useCallback(() => {
    setSearchQuery('');
  }, []);

  const openDeleteConfirmation = (item) => {
    setUserToDelete(item)
  }

  const handleDeleteUser = async () => {
    await removeUserFromStore({ userId: userToDelete.id });
    setUserToDelete(null);
  }

  const tableInfo = [
    { label: t('globals.name'), data: (item) => item.name },
    { label: t('globals.email'), data: (item) => item.email },
    { label: t('globals.deleteUser'), data: (item) => <IconAsButton clickHandle={(e) => openDeleteConfirmation(item)} iconName='Delete' /> }
  ]

  const openInviteDialog = () => {
    setIsModelOpen(true)
  }

  return (
    <SubPage staticElements={
      <PageHeader>
        <PageControls
          onClearSearch={clearSearch}
          onSearch={handleSearch}
          placeholder={t('course.searchUser')}
          openModelButton={
            <Button icon='Invite' onClick={openInviteDialog}>{t('invites.addTeacher')}</Button>
          }
        />
      </PageHeader>
    }>
      {isLoading ? <Loading /> :
        <ListTable
          tableInfo={tableInfo}
          data={localUsers}
          onRowClick={openUser}
          tableName={'users'}
        />}
      <UserModal open={!!modal} isTeacher={true} />
      <ConfirmDelete
        onClose={() => setUserToDelete(null)}
        onAcceptDelete={handleDeleteUser}
        open={!!userToDelete}
        description={t('users.removeTotallyFromAccount', { userName: userToDelete?.name })}
      />
      <InviteTeacher isModalOpen={isModalOpen} closeModal={setIsModelOpen} />
    </SubPage>
  );
};

export default TeachersList;
